import { useEffect, useRef, useState } from 'react';

import {
  getNextStudyCard,
  submitStudyAnswer,
  submitStudyAnswerAndNotify,
  type StudyCard,
  type StudyFeedback,
  type StudyNextResponse,
} from './study.ts';
import {
  canSubmitStudyAnswer,
  isStudyKeyboardOwnedByNativeControl,
  isUnmodifiedStudyKey,
  optionIndexForShortcut,
  selectStudyOptionFromShortcut,
  toggleStudyOption,
} from './study-keyboard.ts';
import { removeLastOrderWord, toggleOrderWord } from './order-words.ts';
import type { OrderWordItem } from './order-words.ts';
import {
  canonicalOrderWords as deriveCanonicalOrderWords,
  choiceOptionStatus,
  orderPositionStatus,
} from './study-feedback.ts';
import { deckScopeKey, type DeckScope } from '../../shared/deck.ts';
import { createLatestRequestCoordinator } from './latest-request.ts';

export function useStudyController({
  deckId,
  onReviewRecorded,
  scope,
}: {
  deckId: string;
  onReviewRecorded?: () => void | Promise<void>;
  scope?: DeckScope;
}) {
  const scopeKey = deckScopeKey(scope);
  const inputRef = useRef<HTMLInputElement>(null);
  const submittingRef = useRef(false);
  const mountedRef = useRef(false);
  const requestCoordinatorRef = useRef(createLatestRequestCoordinator());
  const [card, setCard] = useState<StudyCard | null | undefined>(undefined);
  const [error, setError] = useState(false);
  const [feedback, setFeedback] = useState<StudyFeedback | null>(null);
  const [next, setNext] = useState<StudyNextResponse['card']>(null);
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
  const [orderWorkspace, setOrderWorkspace] = useState<OrderWordItem[]>([]);
  const [textValue, setTextValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function loadNext(recentlyShownCardId?: string) {
    const request = requestCoordinatorRef.current.begin();
    if (!mountedRef.current) {
      request.finish();
      return;
    }
    setCard(undefined);
    setError(false);
    setFeedback(null);
    setNext(null);
    setSelectedOptionIds([]);
    setOrderWorkspace([]);
    setTextValue('');
    try {
      const response = await getNextStudyCard(
        deckId,
        recentlyShownCardId,
        fetch,
        scope,
        request.signal,
      );
      if (!mountedRef.current || !request.isCurrent()) return;
      setCard(response.card);
    } catch {
      if (!mountedRef.current || !request.isCurrent()) return;
      setError(true);
    } finally {
      request.finish();
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestCoordinatorRef.current.cancel();
    };
  }, []);

  useEffect(() => {
    void loadNext();
    return () => requestCoordinatorRef.current.cancel();
  }, [deckId, scopeKey]);

  useEffect(() => {
    if (card?.type === 'text-input') inputRef.current?.focus();
  }, [card]);

  function toggleOption(optionId: string) {
    if (
      !card ||
      card.type === 'text-input' ||
      card.type === 'order-words' ||
      feedback ||
      submitting
    )
      return;
    setSelectedOptionIds((current) =>
      toggleStudyOption(card.type, current, optionId),
    );
  }

  async function submit(selectedOverride?: string[]) {
    const effectiveSelectedOptionIds = selectedOverride ?? selectedOptionIds;
    if (
      !card ||
      feedback ||
      submitting ||
      submittingRef.current ||
      !canSubmitStudyAnswer(
        card,
        textValue,
        effectiveSelectedOptionIds,
        orderWorkspace.length,
      )
    )
      return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const answer =
        card.type === 'text-input'
          ? { type: card.type, value: textValue }
          : card.type === 'choice'
            ? {
                presentedOptionIds: card.options.map((option) => option.id),
                selectedOptionId: effectiveSelectedOptionIds[0] ?? '',
                type: card.type,
              }
            : card.type === 'multi-choice'
              ? {
                  presentedOptionIds: card.options.map((option) => option.id),
                  selectedOptionIds: effectiveSelectedOptionIds,
                  type: card.type,
                }
              : {
                  order: orderWorkspace.map((item) => item.sourceIndex),
                  type: card.type,
                };
      const response = await submitStudyAnswerAndNotify(
        deckId,
        card.id,
        answer,
        onReviewRecorded,
        (requestedDeckId, requestedCardId, submittedAnswer, fetcher) =>
          submitStudyAnswer(
            requestedDeckId,
            requestedCardId,
            submittedAnswer,
            fetcher,
            scope,
          ),
      );
      if (!mountedRef.current) return;
      setFeedback(response.feedback);
      setNext(response.next);
    } catch {
      if (!mountedRef.current) return;
      setError(true);
    } finally {
      submittingRef.current = false;
      if (mountedRef.current) setSubmitting(false);
    }
  }

  function continueStudy() {
    if (!feedback) return;
    if (next) {
      setCard(next);
      setFeedback(null);
      setNext(null);
      setSelectedOptionIds([]);
      setOrderWorkspace([]);
      setTextValue('');
      return;
    }
    setCard(null);
    setFeedback(null);
  }

  function optionStatus(optionId: string) {
    return choiceOptionStatus(feedback, selectedOptionIds, optionId);
  }

  function orderStatus(item: OrderWordItem, position: number) {
    return orderPositionStatus(feedback, orderWorkspace, item, position);
  }

  function canonicalOrderWords() {
    if (!card || card.type !== 'order-words') return undefined;
    return deriveCanonicalOrderWords(card.items, feedback?.correctOrder);
  }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!isUnmodifiedStudyKey(event)) return;
      if (isStudyKeyboardOwnedByNativeControl(event.target)) return;
      if (event.key === 'Enter' && feedback) {
        event.preventDefault();
        continueStudy();
        return;
      }
      if (
        event.key === 'Enter' &&
        card &&
        !feedback &&
        card.type !== 'text-input'
      ) {
        event.preventDefault();
        if (
          canSubmitStudyAnswer(
            card,
            textValue,
            selectedOptionIds,
            orderWorkspace.length,
          )
        )
          void submit();
        return;
      }
      if (!card || feedback || submitting) return;
      if (card.type === 'order-words') {
        if (event.key === 'Backspace') {
          event.preventDefault();
          setOrderWorkspace(
            (current) => removeLastOrderWord(current).workspace,
          );
          return;
        }
        const index = optionIndexForShortcut(event.key, card.items.length);
        const item = index === null ? undefined : card.items[index];
        if (!item) return;
        event.preventDefault();
        setOrderWorkspace((current) => toggleOrderWord(current, item));
        return;
      }
      if (card.type === 'text-input') return;
      const optionIndex = optionIndexForShortcut(
        event.key,
        card.options.length,
      );
      const option =
        optionIndex === null ? undefined : card.options[optionIndex];
      if (!option) return;
      event.preventDefault();
      const shortcutSelection = selectStudyOptionFromShortcut(
        card.type,
        selectedOptionIds,
        option.id,
      );
      setSelectedOptionIds(shortcutSelection.selectedOptionIds);
      if (shortcutSelection.submitImmediately)
        void submit(shortcutSelection.selectedOptionIds);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    card,
    feedback,
    orderWorkspace,
    selectedOptionIds,
    submitting,
    textValue,
    scopeKey,
  ]);

  return {
    card,
    canonicalOrderWords,
    continueStudy,
    error,
    feedback,
    inputRef,
    loadNext,
    optionStatus,
    orderStatus,
    orderWorkspace,
    selectedOptionIds,
    setOrderWorkspace,
    setSelectedOptionIds,
    setTextValue,
    submit,
    submitting,
    textValue,
    toggleOption,
  };
}
